import { type FC, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';

import { trackAbTestV2Variant } from 'dibs-buyer-layout/exports/clientABTestV2';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

import { HpSharedMostSavedItemsCarousel } from '../HpSharedMostSavedItemsCarousel/HpSharedMostSavedItemsCarousel';
import { HpSharedExcellentValuesCarousel } from '../HpSharedExcellentValuesCarousel/HpSharedExcellentValuesCarousel';
import { HpSharedBestsellingDesignsCarousel } from '../HpSharedBestsellingDesignsCarousel/HpSharedBestsellingDesignsCarousel';
import { HpSharedMostSavedItemsCarouselVariant } from '../HpSharedMostSavedItemsCarousel/HpSharedMostSavedItemsCarouselVariant';
import { HpSharedExcellentValuesCarouselVariant } from '../HpSharedExcellentValuesCarousel/HpSharedExcellentValuesCarouselVariant';
import { HpSharedBestsellingDesignsCarouselVariant } from '../HpSharedBestsellingDesignsCarousel/HpSharedBestsellingDesignsCarouselVariant';
import {
    useIsCollectionsCapTestVariant,
    COLLECTIONS_CAP_TEST_NAME,
} from '../helpers/abTestCollectionsCap';

import { type HpSharedLocationsModule_viewer$key } from './__generated__/HpSharedLocationsModule_viewer.graphql';
import { type HpSharedLocationsModule_user$key } from './__generated__/HpSharedLocationsModule_user.graphql';

const viewerFragment = graphql`
    fragment HpSharedLocationsModule_viewer on Viewer {
        ...HpSharedMostSavedItemsCarousel_viewer @skip(if: $isCollectionsCapTestVariant)
        ...HpSharedExcellentValuesCarousel_viewer @skip(if: $isCollectionsCapTestVariant)
        ...HpSharedBestsellingDesignsCarousel_viewer @skip(if: $isCollectionsCapTestVariant)
        ...HpSharedMostSavedItemsCarouselVariant_viewer @include(if: $isCollectionsCapTestVariant)
        ...HpSharedExcellentValuesCarouselVariant_viewer @include(if: $isCollectionsCapTestVariant)
        ...HpSharedBestsellingDesignsCarouselVariant_viewer
            @include(if: $isCollectionsCapTestVariant)
    }
`;

const userFragment = graphql`
    fragment HpSharedLocationsModule_user on User {
        ...HpSharedMostSavedItemsCarousel_user @skip(if: $isCollectionsCapTestVariant)
        ...HpSharedExcellentValuesCarousel_user @skip(if: $isCollectionsCapTestVariant)
        ...HpSharedBestsellingDesignsCarousel_user @skip(if: $isCollectionsCapTestVariant)
        ...HpSharedMostSavedItemsCarouselVariant_user @include(if: $isCollectionsCapTestVariant)
        ...HpSharedExcellentValuesCarouselVariant_user @include(if: $isCollectionsCapTestVariant)
        ...HpSharedBestsellingDesignsCarouselVariant_user @include(if: $isCollectionsCapTestVariant)
    }
`;

export const HpSharedLocationsModule: FC<{
    viewer: HpSharedLocationsModule_viewer$key | null;
    user?: HpSharedLocationsModule_user$key | null;
    isMobile: boolean;
}> = ({ viewer: viewerRef, user: userRef, isMobile }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const modulesRef = useRef(null);
    const isCollectionsCapTestVariant = useIsCollectionsCapTestVariant();

    return (
        <>
            <VisibilityTracker
                elementRef={modulesRef}
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackAbTestV2Variant(COLLECTIONS_CAP_TEST_NAME);
                    }
                }}
            />
            <div ref={modulesRef}>
                {isCollectionsCapTestVariant ? (
                    <>
                        <HpSharedMostSavedItemsCarouselVariant
                            viewer={viewer}
                            user={user}
                            isMobile={isMobile}
                        />
                        <HpSharedExcellentValuesCarouselVariant
                            viewer={viewer}
                            user={user}
                            isMobile={isMobile}
                        />
                        <HpSharedBestsellingDesignsCarouselVariant
                            viewer={viewer}
                            user={user}
                            isMobile={isMobile}
                        />
                    </>
                ) : (
                    <>
                        <HpSharedMostSavedItemsCarousel
                            viewer={viewer}
                            user={user}
                            isMobile={isMobile}
                        />
                        <HpSharedExcellentValuesCarousel
                            viewer={viewer}
                            user={user}
                            isMobile={isMobile}
                        />
                        <HpSharedBestsellingDesignsCarousel
                            viewer={viewer}
                            user={user}
                            isMobile={isMobile}
                        />
                    </>
                )}
            </div>
        </>
    );
};
